import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { REVIEWS } from "@/data/menu";
import { Reveal } from "@/components/site/Reveal";
import ReviewDialog from "@/components/site/ReviewDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Stars({ n, size = 15 }) {
  return (
    <div className="flex gap-0.5">
      {[0, 1, 2, 3, 4].map((s) => (
        <Star key={s} size={size} className={s < n ? "fill-[#DE9A2C] text-[#DE9A2C]" : "text-white/20"} />
      ))}
    </div>
  );
}

function ReviewCard({ r, i, testid }) {
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
      className="flex flex-col rounded-3xl border border-white/10 bg-noir-soft p-6"
    >
      <Quote className="text-amber-brand" size={26} />
      <div className="mt-3"><Stars n={r.rating} /></div>
      <p className="mt-3 text-white/70 leading-relaxed flex-1">“{r.text}”</p>
      {r.photo ? <img src={r.photo} alt="review" className="mt-4 rounded-xl h-36 w-full object-cover border border-white/10" /> : null}
      <div className="mt-3 text-xs uppercase tracking-[0.12em] text-amber-brand font-bold">On {r.item}</div>
      <div className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4">
        {r.avatar ? (
          <img src={r.avatar} alt={r.name} className="w-11 h-11 rounded-full object-cover" />
        ) : (
          <span className="grid place-items-center w-11 h-11 rounded-full bg-[#8A2231] text-white font-display font-bold">{r.name?.[0]?.toUpperCase() || "?"}</span>
        )}
        <div>
          <div className="font-semibold text-white">{r.name}</div>
          <div className="text-sm text-white/50">{r.role}</div>
        </div>
      </div>
    </motion.div>
  );
}

export default function ReviewsSection() {
  const [userReviews, setUserReviews] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => setUserReviews(res.data)).catch(() => {});
  }, []);

  const onCreated = (rv) => setUserReviews((prev) => [rv, ...prev]);

  const mappedUser = userReviews.map((rv) => ({
    name: rv.name, role: "Verified customer", rating: rv.rating, item: rv.item_name, text: rv.comment, photo: rv.photo, avatar: "",
  }));
  const all = [...mappedUser, ...REVIEWS];
  const avg = (all.reduce((s, r) => s + r.rating, 0) / all.length).toFixed(1);

  return (
    <section id="reviews" data-testid="reviews-section" className="bg-noir text-white noise relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <Reveal className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-brand">Loved by the community</p>
            <h2 className="mt-3 font-display text-4xl sm:text-6xl font-bold tracking-tight text-white">Real people. Real macros.</h2>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Stars n={5} size={20} />
              <span className="font-mono text-lg font-bold text-white" data-testid="reviews-avg">{avg}/5</span>
              <span className="text-white/60">from 1,200+ verified reviews</span>
            </div>
          </Reveal>
          <ReviewDialog onCreated={onCreated} />
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="reviews-grid">
          {all.slice(0, 8).map((r, i) => (
            <ReviewCard key={`${r.name}-${i}`} r={r} i={i} testid={`review-${i}`} />
          ))}
        </div>
      </div>
    </section>
  );
}
